import { useEffect, useState } from 'react';
import projectQueries from '../../../../requests/queries/ProjectsQuesries';
import './CommandDisplayPanel.css';

const DISPLAY_APP_URL =
  process.env.REACT_APP_COMMANDS_DISPLAY_URL || 'http://localhost:3002';

const API_URL =
  process.env.REACT_APP_API_URI || 'https://kiosk-pluracore-60922b42b03b.herokuapp.com';

function buildUrl(path, projectId, token) {
  const params = new URLSearchParams({
    project_id: projectId,
    token,
    api_url: API_URL,
  });
  return `${DISPLAY_APP_URL}${path}?${params.toString()}`;
}

function UrlRow({ label, url, copied, onCopy }) {
  return (
    <div className="command-display-panel__field">
      <span className="command-display-panel__label">{label}</span>
      <div className="command-display-panel__row">
        <code className="command-display-panel__code">{url}</code>
        <button
          type="button"
          className="command-display-panel__btn"
          onClick={() => onCopy(url)}
        >
          {copied ? 'Copié' : 'Copier'}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="command-display-panel__btn command-display-panel__btn--link"
        >
          Ouvrir
        </a>
      </div>
    </div>
  );
}

export default function CommandDisplayModal({ projectId, isOpen, onClose }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);

  const [ncConfig, setNcConfig] = useState({ enabled: false, base_url: '', token_configured: false });
  const [ncToken, setNcToken] = useState('');
  const [ncSaving, setNcSaving] = useState(false);
  const [ncTesting, setNcTesting] = useState(false);
  const [ncMessage, setNcMessage] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [data, ncData] = await Promise.all([
        projectQueries.getCommandDisplayInfo(projectId),
        projectQueries.getNewCactusConfig(projectId),
      ]);
      setInfo(data);
      setNcConfig(ncData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && projectId) {
      load();
    }
  }, [isOpen, projectId]);

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch {
      // ignore
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Régénérer le token ? Les écrans devront être reconfigurés.')) return;
    setRegenerating(true);
    try {
      const data = await projectQueries.regenerateCommandDisplayToken(projectId);
      setInfo((prev) => ({ ...prev, command_display_token: data.command_display_token }));
    } finally {
      setRegenerating(false);
    }
  };

  const handleSaveNewCactus = async () => {
    setNcSaving(true);
    setNcMessage(null);
    try {
      const payload = {
        enabled: ncConfig.enabled,
        base_url: ncConfig.base_url,
        ...(ncToken.trim() ? { token: ncToken.trim() } : {}),
      };
      const data = await projectQueries.updateNewCactusConfig(projectId, payload);
      setNcConfig(data);
      setNcToken('');
      setNcMessage({ type: 'success', text: 'Configuration New Cactus enregistrée.' });
    } catch (err) {
      setNcMessage({ type: 'error', text: err?.response?.data?.message || 'Erreur lors de la sauvegarde' });
    } finally {
      setNcSaving(false);
    }
  };

  const handleTestNewCactus = async () => {
    setNcTesting(true);
    setNcMessage(null);
    try {
      if (ncToken.trim() || ncConfig.base_url) {
        await projectQueries.updateNewCactusConfig(projectId, {
          enabled: true,
          base_url: ncConfig.base_url,
          ...(ncToken.trim() ? { token: ncToken.trim() } : {}),
        });
      }
      const result = await projectQueries.testNewCactusConnection(projectId);
      setNcMessage({
        type: 'success',
        text: result.ok
          ? `Connexion OK${result.currency ? ` (devise : ${result.currency})` : ''}`
          : 'Configuration incomplète',
      });
      const refreshed = await projectQueries.getNewCactusConfig(projectId);
      setNcConfig(refreshed);
    } catch (err) {
      setNcMessage({ type: 'error', text: err?.response?.data?.message || 'Test de connexion échoué' });
    } finally {
      setNcTesting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const kitchenUrl = info
    ? buildUrl('/', info.project_id, info.command_display_token)
    : '';
  const pickupUrl = info
    ? buildUrl('/pickup', info.project_id, info.command_display_token)
    : '';

  return (
    <div className="command-display-overlay" onClick={handleOverlayClick}>
      <div
        className="command-display-modal"
        role="dialog"
        aria-labelledby="command-display-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="command-display-modal__header">
          <div>
            <h2 id="command-display-modal-title">📺 Écrans commandes</h2>
            <p className="command-display-modal__subtitle">
              Cuisine (<code>/</code>) et écran client prêtes (<code>/pickup</code>).
              Plusieurs tablettes peuvent partager le même projet.
            </p>
          </div>
          <button
            type="button"
            className="command-display-modal__close"
            onClick={onClose}
            aria-label="Fermer"
          >
            ✕
          </button>
        </header>

        <div className="command-display-modal__body">
          {loading ? (
            <p className="command-display-panel__loading">Chargement…</p>
          ) : (
            <>
              <UrlRow
                label="Écran cuisine (3 colonnes)"
                url={kitchenUrl}
                copied={copiedUrl === kitchenUrl}
                onCopy={copy}
              />

              <UrlRow
                label="Écran client — commandes prêtes (user_datas)"
                url={pickupUrl}
                copied={copiedUrl === pickupUrl}
                onCopy={copy}
              />

              <div className="command-display-panel__field">
                <span className="command-display-panel__label">Token d&apos;affichage</span>
                <div className="command-display-panel__row">
                  <code className="command-display-panel__code command-display-panel__code--token">
                    {info?.command_display_token}
                  </code>
                  <button
                    type="button"
                    className="command-display-panel__btn"
                    onClick={() => copy(info?.command_display_token)}
                  >
                    {copiedUrl === info?.command_display_token ? 'Copié' : 'Copier'}
                  </button>
                  <button
                    type="button"
                    className="command-display-panel__btn command-display-panel__btn--danger"
                    onClick={handleRegenerate}
                    disabled={regenerating}
                  >
                    {regenerating ? '…' : 'Régénérer'}
                  </button>
                </div>
              </div>

              <hr className="command-display-panel__divider" />

              <h3 className="command-display-panel__section-title">New Cactus (caisse / RRA)</h3>
              <p className="command-display-modal__subtitle">
                Jeton et URL restent côté serveur — jamais exposés sur la borne.
                Associez chaque produit CMS à un <code>menu_item_id</code> New Cactus.
              </p>

              <div className="command-display-panel__field">
                <label className="command-display-panel__label">
                  <input
                    type="checkbox"
                    checked={ncConfig.enabled}
                    onChange={(e) => setNcConfig((prev) => ({ ...prev, enabled: e.target.checked }))}
                  />
                  {' '}Activer New Cactus pour ce projet
                </label>
              </div>

              <div className="command-display-panel__field">
                <span className="command-display-panel__label">Base URL API kiosk</span>
                <input
                  type="url"
                  className="command-display-panel__input"
                  placeholder="https://commande.example.rw/api/kiosk"
                  value={ncConfig.base_url}
                  onChange={(e) => setNcConfig((prev) => ({ ...prev, base_url: e.target.value }))}
                />
              </div>

              <div className="command-display-panel__field">
                <span className="command-display-panel__label">
                  Jeton borne {ncConfig.token_configured ? '(configuré — laisser vide pour conserver)' : ''}
                </span>
                <input
                  type="password"
                  className="command-display-panel__input"
                  placeholder="Bearer token (48 caractères)"
                  value={ncToken}
                  onChange={(e) => setNcToken(e.target.value)}
                  autoComplete="off"
                />
              </div>

              {ncMessage && (
                <p className={`command-display-panel__message command-display-panel__message--${ncMessage.type}`}>
                  {ncMessage.text}
                </p>
              )}

              <div className="command-display-panel__row command-display-panel__row--actions">
                <button
                  type="button"
                  className="command-display-panel__btn"
                  onClick={handleSaveNewCactus}
                  disabled={ncSaving}
                >
                  {ncSaving ? '…' : 'Enregistrer New Cactus'}
                </button>
                <button
                  type="button"
                  className="command-display-panel__btn"
                  onClick={handleTestNewCactus}
                  disabled={ncTesting}
                >
                  {ncTesting ? '…' : 'Tester la connexion'}
                </button>
              </div>
            </>
          )}
        </div>

        <footer className="command-display-modal__footer">
          <button type="button" className="command-display-panel__btn" onClick={onClose}>
            Fermer
          </button>
        </footer>
      </div>
    </div>
  );
}
